<template>
  <div class="cp-page">
    <!-- ===== Hero ===== -->
    <section class="cp-hero">
      <div class="cp-hero__blob cp-hero__blob--one" />
      <div class="cp-hero__blob cp-hero__blob--two" />
      <div class="cp-hero__content">
        <div class="cp-hero__icon">
          <feather-icon
            :icon="icon"
            size="28"
          />
        </div>
        <div>
          <h2 class="cp-hero__title">
            {{ title }}
          </h2>
          <p class="cp-hero__subtitle">
            {{ subtitle }}
          </p>
        </div>
      </div>
      <div class="cp-hero__side">
        <div class="cp-hero__stat">
          <span class="cp-hero__stat-value">{{ totalRows }}</span>
          <span class="cp-hero__stat-label">Total</span>
        </div>
        <button
          type="button"
          class="cp-hero__refresh"
          :disabled="dataLoad"
          @click="fetchData(page)"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            :class="{ 'cp-spin': dataLoad }"
          />
          Refresh
        </button>
      </div>
    </section>

    <!-- ===== Toolbar ===== -->
    <section class="cp-toolbar">
      <div class="cp-search">
        <feather-icon
          icon="SearchIcon"
          size="15"
          class="cp-search__icon"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="cp-search__input"
          placeholder="Search complaints…"
          @input="onSearchInput"
        >
        <button
          v-if="searchQuery"
          type="button"
          class="cp-search__clear"
          @click="clearSearch"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>

      <div class="cp-chips">
        <button
          v-for="chip in statusChips"
          :key="chip.key"
          type="button"
          class="cp-chip"
          :class="[`cp-tone--${chip.tone}`, { 'is-active': activeStatus === chip.key }]"
          @click="activeStatus = chip.key"
        >
          <feather-icon
            :icon="chip.icon"
            size="13"
          />
          {{ chip.label }}
          <span class="cp-chip__count">{{ chip.count }}</span>
        </button>
      </div>
    </section>

    <!-- ===== States ===== -->
    <div
      v-if="dataLoad && !rows.length"
      class="cp-grid"
    >
      <div
        v-for="n in 6"
        :key="n"
        class="cp-card cp-card--skeleton"
      >
        <div class="cp-skel cp-skel--sm" />
        <div class="cp-skel cp-skel--lg" />
        <div class="cp-skel" />
        <div class="cp-skel" />
        <div class="cp-skel cp-skel--md" />
      </div>
    </div>

    <div
      v-else-if="!visibleRows.length"
      class="cp-empty"
    >
      <div class="cp-empty__icon">
        <feather-icon
          icon="InboxIcon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        {{ searchQuery || activeStatus !== 'all' ? 'No matching complaints' : 'No complaints yet' }}
      </h5>
      <p class="mb-0 text-muted">
        {{ searchQuery || activeStatus !== 'all' ? 'Try a different search or filter.' : 'Complaints will show up here once they are available.' }}
      </p>
    </div>

    <!-- ===== Cards ===== -->
    <div
      v-else
      class="cp-grid"
      :class="{ 'is-loading': dataLoad }"
    >
      <article
        v-for="row in visibleRows"
        :key="row.id"
        class="cp-card"
        :class="`cp-tone--${statusMeta(row.status).tone}`"
      >
        <div class="cp-card__bar" />

        <header class="cp-card__head">
          <span class="cp-id">
            <feather-icon
              icon="HashIcon"
              size="13"
            />
            {{ row.complaint_id || row.id }}
          </span>
          <span class="cp-status">
            <feather-icon
              :icon="statusMeta(row.status).icon"
              size="13"
            />
            {{ statusMeta(row.status).label(row.status) }}
          </span>
        </header>

        <h4 class="cp-card__title">
          {{ row.type_of_complaint || 'Complaint' }}
        </h4>
        <div class="cp-card__meta">
          <span
            v-if="row.type_of_service"
            class="cp-meta"
          >
            <feather-icon
              icon="BriefcaseIcon"
              size="13"
            />
            {{ row.type_of_service }}
          </span>
          <span
            v-if="row.request_id"
            class="cp-meta"
          >
            <feather-icon
              icon="FileTextIcon"
              size="13"
            />
            Request {{ row.request_id }}
          </span>
        </div>
        <p
          class="cp-card__desc"
          :title="row.description"
        >
          {{ row.description || 'No description provided.' }}
        </p>

        <!-- Progress -->
        <div
          v-if="statusMeta(row.status).step >= 0"
          class="cp-steps"
        >
          <div
            v-for="(step, i) in steps"
            :key="step"
            class="cp-step"
            :class="{
              'is-done': i < statusMeta(row.status).step,
              'is-current': i === statusMeta(row.status).step
            }"
          >
            <span class="cp-step__dot" />
            <span class="cp-step__label">{{ step }}</span>
          </div>
        </div>
        <div
          v-else
          class="cp-banner"
        >
          <feather-icon
            icon="AlertOctagonIcon"
            size="14"
          />
          This complaint was rejected
        </div>

        <!-- Client -->
        <div class="cp-block">
          <div class="cp-block__title">
            Client
          </div>
          <div class="cp-lines">
            <span class="cp-line">
              <feather-icon
                icon="UserIcon"
                size="14"
              />
              {{ row.client_name || '—' }}
            </span>
            <a
              v-if="row.email"
              :href="`mailto:${row.email}`"
              class="cp-line cp-line--link"
            >
              <feather-icon
                icon="MailIcon"
                size="14"
              />
              {{ row.email }}
            </a>
            <a
              v-if="row.client_number"
              :href="`tel:${row.client_number}`"
              class="cp-line cp-line--link"
            >
              <feather-icon
                icon="PhoneIcon"
                size="14"
              />
              {{ row.client_number }}
            </a>
          </div>
        </div>

        <!-- People -->
        <div class="cp-people">
          <div
            v-for="person in people(row)"
            :key="person.role"
            class="cp-person"
            :class="{ 'is-empty': !person.name }"
          >
            <div class="cp-person__avatar">
              {{ person.name ? avatarText(person.name) : '?' }}
            </div>
            <div class="cp-person__text">
              <span class="cp-person__role">{{ person.role }}</span>
              <span
                class="cp-person__name"
                :title="person.email"
              >
                {{ person.name || 'Not assigned yet' }}
              </span>
              <span
                v-if="person.office"
                class="cp-person__office"
              >
                {{ person.office }}
              </span>
            </div>
          </div>
        </div>

        <!-- Details -->
        <button
          type="button"
          class="cp-more"
          @click="toggleDetails(row.id)"
        >
          {{ isExpanded(row.id) ? 'Hide details' : 'Show details' }}
          <feather-icon
            :icon="isExpanded(row.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="14"
          />
        </button>

        <div
          v-if="isExpanded(row.id)"
          class="cp-details"
        >
          <div class="cp-flags">
            <div
              v-for="flag in flags(row)"
              :key="flag.label"
              class="cp-flag"
              :class="`cp-flag--${flag.state}`"
            >
              <feather-icon
                :icon="flag.icon"
                size="14"
              />
              <span class="cp-flag__label">{{ flag.label }}</span>
              <span class="cp-flag__value">{{ flag.value }}</span>
            </div>
          </div>

          <div
            v-for="item in detailItems(row)"
            :key="item.label"
            class="cp-detail"
          >
            <span class="cp-detail__label">
              <feather-icon
                :icon="item.icon"
                size="13"
              />
              {{ item.label }}
            </span>
            <span class="cp-detail__value">{{ item.value }}</span>
          </div>
        </div>

        <!-- Actions -->
        <footer
          v-if="rowAction(row)"
          class="cp-card__foot"
        >
          <button
            type="button"
            class="cp-btn"
            @click="rowAction(row).run(row)"
          >
            <feather-icon
              :icon="rowAction(row).icon"
              size="15"
            />
            {{ rowAction(row).label }}
            <feather-icon
              icon="ArrowRightIcon"
              size="14"
            />
          </button>
        </footer>
      </article>
    </div>

    <!-- ===== Pagination ===== -->
    <div
      v-if="totalRows > perPage"
      class="cp-pagination"
    >
      <b-pagination
        :value="page"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        first-number
        last-number
        class="mb-0"
        @change="fetchData"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

const STEPS = ['Pending', 'Assigned', 'Responsible', 'Verify', 'Closed']

const STATUS_META = {
  pending: {
    tone: 'amber', icon: 'ClockIcon', step: 0, label: () => 'Pending',
  },
  'investigating by assigned employee': {
    tone: 'blue', icon: 'SearchIcon', step: 1, label: () => 'Investigating · Assigned',
  },
  'investigating by responsible employee': {
    tone: 'violet', icon: 'UserCheckIcon', step: 2, label: () => 'Investigating · Responsible',
  },
  'awaiting verification': {
    tone: 'teal', icon: 'CheckSquareIcon', step: 3, label: () => 'Awaiting verification',
  },
  closed: {
    tone: 'green', icon: 'CheckCircleIcon', step: 4, label: () => 'Closed',
  },
  rejected: {
    tone: 'rose', icon: 'XCircleIcon', step: -1, label: () => 'Rejected',
  },
}

const FALLBACK_META = {
  tone: 'indigo', icon: 'HelpCircleIcon', step: 0, label: status => status || 'Unknown',
}

export default {
  name: 'ComplaintList',
  props: {
    // 'index' | 'assigned' | 'responsible'
    mode: {
      type: String,
      default: 'index',
    },
    apiUrl: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: 'Complaints',
    },
    subtitle: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: 'AlertOctagonIcon',
    },
  },
  data() {
    return {
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      searchQuery: '',
      activeStatus: 'all',
      expanded: {},
      steps: STEPS,
      searchTimer: null,
    }
  },
  computed: {
    visibleRows() {
      if (this.activeStatus === 'all') return this.rows
      return this.rows.filter(row => row.status === this.activeStatus)
    },
    statusChips() {
      const counts = {}
      this.rows.forEach(row => {
        counts[row.status] = (counts[row.status] || 0) + 1
      })
      const chips = Object.keys(counts).map(status => {
        const meta = this.statusMeta(status)
        return {
          key: status,
          label: meta.label(status),
          icon: meta.icon,
          tone: meta.tone,
          count: counts[status],
        }
      })
      return [
        {
          key: 'all', label: 'All', icon: 'LayersIcon', tone: 'indigo', count: this.rows.length,
        },
        ...chips,
      ]
    },
  },
  mounted() {
    this.fetchData(1)
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    statusMeta(status) {
      return STATUS_META[status] || FALLBACK_META
    },
    avatarText(name) {
      return String(name || '?')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    isExpanded(id) {
      return Boolean(this.expanded[id])
    },
    toggleDetails(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetchData(1), 400)
    },
    clearSearch() {
      this.searchQuery = ''
      this.fetchData(1)
    },
    people(row) {
      const assigned = row.assigned_employee || {}
      const responsible = row.responsible_employee || {}
      return [
        {
          role: 'Assigned', name: assigned.name, email: assigned.email, office: assigned.office?.name,
        },
        {
          role: 'Responsible', name: responsible.name, email: responsible.email, office: responsible.office?.name,
        },
      ]
    },
    flagState(value) {
      if (value === null || value === undefined) return 'pending'
      return value ? 'yes' : 'no'
    },
    flags(row) {
      const defs = [
        { label: 'Valid', key: 'valid' },
        { label: 'Accepted by responsible', key: 'accepted_by_responsible' },
        { label: 'Satisfactory', key: 'satisfactory' },
      ]
      const iconByState = { yes: 'CheckCircleIcon', no: 'XCircleIcon', pending: 'ClockIcon' }
      const textByState = { yes: 'Yes', no: 'No', pending: 'Pending' }
      return defs.map(def => {
        const state = this.flagState(row[def.key])
        return {
          label: def.label, state, icon: iconByState[state], value: textByState[state],
        }
      })
    },
    detailItems(row) {
      const items = [
        { label: 'Category', icon: 'TagIcon', value: row.category },
        {
          label: 'Next follow up',
          icon: 'CalendarIcon',
          value: row.next_follow_up ? (formatDate(row.next_follow_up) || row.next_follow_up) : '',
        },
        { label: 'Remarks', icon: 'MessageSquareIcon', value: row.remarks },
        { label: 'Root cause', icon: 'AlertTriangleIcon', value: row.root_cause },
        { label: 'Corrective action', icon: 'ToolIcon', value: row.corrective_action },
      ]
      return items.filter(item => item.value)
    },
    rowAction(row) {
      if (this.mode === 'assigned') {
        return {
          label: 'Handle as assigned',
          icon: 'ClipboardIcon',
          run: item => this.$router.push({ name: 'add-assigned', params: { id: item.id } }),
        }
      }
      if (this.mode === 'responsible') {
        return {
          label: 'Handle as responsible',
          icon: 'ShieldIcon',
          run: item => this.$router.push({ name: 'add-responsible', params: { id: item.id } }),
        }
      }
      if (row.status === 'pending') {
        return {
          label: 'Admin initial review',
          icon: 'ClipboardIcon',
          run: item => this.$router.push({ name: 'admin-initial', params: { id: item.id } }),
        }
      }
      if (row.status === 'awaiting verification') {
        return {
          label: 'Admin verify',
          icon: 'CheckCircleIcon',
          run: item => this.awaitingVerification(item.id),
        }
      }
      return null
    },
    async fetchData(page = 1) {
      this.page = page
      this.dataLoad = true
      try {
        const params = { skip: (page - 1) * this.perPage }
        const search = this.searchQuery.trim()
        if (search) params.search = search
        if (localStorage.getItem('accessRole') === 'office-manager') {
          params.office_id = localStorage.getItem('office_id')
        }

        const response = await axios.get(this.apiUrl, { params })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data
          const list = Array.isArray(data) ? data : Object.values(data || {}).find(Array.isArray)
          this.rows = list || []
          this.totalRows = Number(data?.count) || this.rows.length
        }
      } catch (error) {
        this.rows = []
        this.totalRows = 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load complaints',
            icon: 'BellIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.dataLoad = false
      }
    },
    awaitingVerification(id) {
      this.$swal({
        title: 'Admin Verify',
        width: 600,
        html: `
    <div style="display: flex; flex-direction: column; gap: 15px; align-items: start;">
      <label for="nextFollowUp">Next Follow Up Date:</label>
      <input type="date" id="nextFollowUp" class="swal2-input" style="width: 100%;"/>

      <div style="display: flex; align-items: center;">
        <input type="checkbox" id="satisfactory" />
        <label for="satisfactory" style="margin-left: 5px; font-size: 18px;">Satisfactory</label>
      </div>
    </div>
  `,
        showCancelButton: true,
        confirmButtonText: 'Verify',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#dc2626',
        preConfirm: () => {
          const selectednextFollowUp = document.getElementById('nextFollowUp').value
          const selectedSatisfactory = document.getElementById('satisfactory').checked
          if (!selectednextFollowUp || !selectedSatisfactory) {
            this.$swal.showValidationMessage('next follow up and satisfactory is required!')
            return false
          }
          return { selectednextFollowUp, selectedSatisfactory }
        },
      }).then(result => {
        if (result.isConfirmed) {
          this.$swal.fire({
            title: 'Verifying...',
            allowOutsideClick: false,
            didOpen: () => {
              this.$swal.showLoading()
            },
          })

          const payload = {
            satisfactory: result.value.selectedSatisfactory,
            next_follow_up: result.value.selectednextFollowUp,
          }

          axios.post(`/complaints/${id}/verify`, payload)
            .then(() => {
              this.$swal.close()
              this.$toast.success('complaint verified successfully!')
              this.fetchData(this.page)
            })
            .catch(() => {
              this.$swal.close()
              this.$toast.error('Failed to verified.')
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped src="./ComplaintList.scss"></style>
